import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import extensionPackage from 'virtual:extension-package'
import { useTranslation } from '../hooks/useTranslation'
import { placeHelpPanel } from '../utils/extensionDownloadPlacement'
import './ExtensionDownload.css'

const SHOW_DELAY_MS = 500
const HIDE_DELAY_MS = 160

function ExtensionDownload({ variant = 'banner' }) {
  const { t } = useTranslation()
  const helpId = useId()
  const rootRef = useRef(null)
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  const showTimerRef = useRef(null)
  const hideTimerRef = useRef(null)
  const pointerTypeRef = useRef('mouse')
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const { href, fileName, version, contentHash, source } = extensionPackage
  const label = t('downloadExtension')
  const helpLabel = t('installExtensionHelp')
  const downloadHref = `${href}?h=${encodeURIComponent(contentHash || version)}`

  const isInHelpTree = useCallback((node) => (
    !!(rootRef.current?.contains(node) || panelRef.current?.contains(node))
  ), [])

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current)
      showTimerRef.current = null
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const closeHelp = useCallback(() => {
    clearTimers()
    setOpen(false)
  }, [clearTimers])

  const openHelp = useCallback(() => {
    clearTimers()
    setOpen(true)
  }, [clearTimers])

  const scheduleShow = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
    if (open) return
    if (showTimerRef.current) return
    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null
      setOpen(true)
    }, SHOW_DELAY_MS)
  }, [open])

  const scheduleHide = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current)
      showTimerRef.current = null
    }
    if (hideTimerRef.current) return
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null
      setOpen(false)
    }, HIDE_DELAY_MS)
  }, [])

  const updatePosition = useCallback(() => {
    const button = btnRef.current
    const panel = panelRef.current
    if (!button) return
    setCoords(placeHelpPanel({
      anchor: button.getBoundingClientRect(),
      panelHeight: panel?.offsetHeight || 200,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      align: variant === 'header' ? 'end' : 'center',
    }))
  }, [variant])

  useLayoutEffect(() => {
    if (!open) return undefined
    updatePosition()
    const frame = window.requestAnimationFrame(updatePosition)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open, updatePosition])

  useEffect(() => () => clearTimers(), [clearTimers])

  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeHelp()
      }
    }
    const onPointerDown = (event) => {
      if (isInHelpTree(event.target)) return
      closeHelp()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open, closeHelp, isInHelpTree])

  useEffect(() => {
    const element = btnRef.current
    if (!element) return undefined

    const handleMouseMove = (event) => {
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      const specular = event.currentTarget.querySelector('.glass-specular')
      if (specular) {
        specular.style.background = `radial-gradient(
          circle at ${x}px ${y}px,
          rgba(255,255,255,0.15) 0%,
          rgba(255,255,255,0.05) 30%,
          rgba(255,255,255,0) 60%
        )`
      }
    }

    const handleMouseLeave = (event) => {
      const specular = event.currentTarget.querySelector('.glass-specular')
      if (specular) specular.style.background = 'none'
    }

    element.addEventListener('mousemove', handleMouseMove)
    element.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      element.removeEventListener('mousemove', handleMouseMove)
      element.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [])

  const isHoverPointer = (pointerType) => pointerType === 'mouse' || pointerType === 'pen'

  return (
    <div ref={rootRef} className={`extension-download is-${variant}${open ? ' is-help-open' : ''}`}>
      <a
        ref={btnRef}
        className={`glass-button extension-download-btn ${variant === 'header' ? 'btn-user-icon' : ''}`}
        href={downloadHref}
        download={fileName}
        aria-label={`${label} (version ${version}, empreinte ${contentHash || 'locale'})`}
        aria-describedby={open ? helpId : undefined}
        aria-expanded={open}
        aria-controls={helpId}
        onPointerDown={(event) => {
          pointerTypeRef.current = event.pointerType || 'mouse'
        }}
        onPointerEnter={(event) => {
          if (!isHoverPointer(event.pointerType)) return
          scheduleShow()
        }}
        onPointerLeave={(event) => {
          if (!isHoverPointer(event.pointerType)) return
          scheduleHide()
        }}
        onFocus={openHelp}
        onBlur={(event) => {
          if (isInHelpTree(event.relatedTarget)) return
          scheduleHide()
        }}
        onClick={() => {
          if (pointerTypeRef.current === 'touch' || window.matchMedia('(hover: none)').matches) {
            openHelp()
          }
        }}
      >
        <div className="glass-filter"></div>
        <div className="glass-overlay"></div>
        <div className="glass-specular"></div>
        <span className="glass-content">
          <img src="/Icon download.png" alt="" />
          {variant !== 'header' && <span className="extension-download-label">{label}</span>}
        </span>
      </a>
      {open && createPortal(
        <div
          ref={panelRef}
          id={helpId}
          className="extension-install-panel"
          role="region"
          aria-label={helpLabel}
          style={coords ? {
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            width: `${coords.width}px`,
            maxHeight: `${coords.maxHeight}px`,
          } : { visibility: 'hidden' }}
          data-placement={coords?.placement || 'bottom'}
          onPointerEnter={(event) => {
            if (!isHoverPointer(event.pointerType)) return
            if (hideTimerRef.current) {
              clearTimeout(hideTimerRef.current)
              hideTimerRef.current = null
            }
          }}
          onPointerLeave={(event) => {
            if (!isHoverPointer(event.pointerType)) return
            scheduleHide()
          }}
        >
          <div className="extension-install-panel__glass" aria-hidden="true" />
          <div className="extension-install-panel__content">
            <p>
              Le fichier ZIP n'installe pas automatiquement l'extension au sein du répertoire de Google Chrome.
              Compatible avec Chrome et Edge (manifeste V3) :
            </p>
            <ol>
              <li>Extrayez l’archive dans un dossier à conserver.</li>
              <li>
                Ouvrez la page des extensions : <code>chrome://extensions</code> (Chrome)
                ou <code>edge://extensions</code> (Edge).
              </li>
              <li>Activez le mode développeur.</li>
              <li>
                Choisissez « Charger l’extension non empaquetée », puis le dossier
                qui contient <code>manifest.json</code>.
              </li>
            </ol>
            <p className="extension-install-note">
              Il ne s’agit pas d’une installation depuis le Chrome Web Store ni
              Microsoft Edge Add-ons.
            </p>
            <p className="extension-install-note">
              Archive locale {version}
              {contentHash ? ` · empreinte ${contentHash}` : ''}.
              {source === 'local-workspace'
                ? ''
                : ' Vérifiez l’empreinte avant d’installer une archive téléchargée ailleurs.'}
            </p>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default ExtensionDownload
